import { ref } from 'vue';
import axios from 'axios';
import { jwtDecode } from 'jwt-decode';
import store from '@/store';
import { APP_BACK_PROTOCOL } from '@/constants/appBackRoutePolicy';
import { postToApp } from '@/composables/useRnBridge';
import { appBackCoordinator } from '@/utils/appBackCoordinator';
import { applyAuthUserIdHeader, writeAuthUserId } from '@/utils/authUserId';

// =============== 딥링크 경로 변환 ===============

const BOARD_ROUTE_MAP = {
  2325: (id) => id ? `/nangman-center/notice/${id}` : '/nangman-center/notice',
  2480: (id) => id ? `/nangman-center/event/${id}` : '/nangman-center/event',
  2434: (id) => id ? `/nangman-center/faq/${id}` : '/nangman-center/faq',
  2520: (id) => id ? `/nangman-center/word/${id}` : '/nangman-center/word',
  2509: (id) => id ? `/nangman-center/essay/${id}` : '/nangman-center/essay',
  2508: (id) => id ? `/nangman-center/counseling/${id}` : '/nangman-center/counseling',
  2336: (id) => id ? `/mypage/inquiryView/inquiry/${id}` : '/mypage/inquiry',
  2522: (id) => id ? `/mypage/inquiryView/eventinquiry/${id}` : '/mypage/inquiry',
};

const resolveDeeplinkPath = ({ source, path, deeplinkData }) => {
  if (source === 'universal' && path) {
    if (path.startsWith('/coupon')) {
      const serial = new URLSearchParams(path.split('?')[1]).get('serial');
      return serial ? `/purchase/gift-box?tab=coupon&serial=${serial}` : '/purchase/gift-box?tab=coupon';
    }
    if (path.startsWith('/gift/')) {
      const code = path.replace('/gift/', '');
      return `/purchase/gift-claim?code=${code}`;
    }
    return path;
  }

  if ((source === 'fcm' || source === 'notification') && deeplinkData) {
    const { boardId, articleId, serviceType, extra } = deeplinkData;
    if (serviceType === 'GIFT') {
      const giftId = extra?.giftId;
      return giftId ? `/purchase/gift-box?tab=inbox&id=${giftId}` : '/purchase/gift-box?tab=inbox';
    }
    const routeFn = BOARD_ROUTE_MAP[boardId];
    if (routeFn) return routeFn(articleId);
    return null;
  }

  if (source === 'branch' && deeplinkData) {
    const { screen, tab, articleId, productId } = deeplinkData;
    if (!screen) return null;
    switch (screen) {
      case 'notice': return articleId ? `/nangman-center/notice/${articleId}` : '/nangman-center/notice';
      case 'event': return articleId ? `/nangman-center/event/${articleId}` : '/nangman-center/event';
      case 'essay': return articleId ? `/nangman-center/essay/${articleId}` : '/nangman-center/essay';
      case 'word': return articleId ? `/nangman-center/word/${articleId}` : '/nangman-center/word';
      case 'faq': return articleId ? `/nangman-center/faq/${articleId}` : '/nangman-center/faq';
      case 'inquiry': return articleId ? `/mypage/inquiryView/inquiry/${articleId}` : '/mypage/inquiry';
      case 'eventinquiry': return articleId ? `/mypage/inquiryView/eventinquiry/${articleId}` : '/mypage/inquiry';
      case 'points': return '/purchase/points';
      case 'tickets': return productId ? `/purchase/tickets/${productId}` : '/purchase/tickets';
      case 'gift-box': return tab ? `/purchase/gift-box?tab=${tab}` : '/purchase/gift-box';
      default: return `/${screen}`;
    }
  }

  return path || null;
};

// =============== 메시지 파싱 ===============

const parseMessage = (event) => {
  try {
    const data = typeof event.data === 'string' ? JSON.parse(event.data) : event.data;
    if (data.source === 'react-devtools-content-script') return null;
    if (data.key === '__VUE_DEVTOOLS_VUE_DETECTED_EVENT__') return null;
    return data;
  } catch (e) {
    console.error('Invalid message received:', `${e}`);
    return null;
  }
};

// =============== 글로벌 상태 (싱글턴) ===============

const accessToken = ref(null);
const authUserId = ref(null);
const iapProducts = ref(null);
let _router = null;
let _initialized = false;
let _authReady = false;
const _pendingDeeplinks = [];
let _lastDeeplinkPath = null;
let _lastDeeplinkTime = 0;

/** accessToken/authUserId 설정 */
const setAuth = (payload) => {
  if (payload?.accessToken) {
    const rawToken = String(payload.accessToken).replace(/^bearer\s+/i, '');
    accessToken.value = payload.accessToken;
    axios.defaults.headers.common['Authorization'] = payload.accessToken;
    sessionStorage.setItem('X-Token', rawToken);

    try {
      const decoded = jwtDecode(rawToken);
      if (!payload.authUserId && decoded.id) payload.authUserId = decoded.id;
      sessionStorage.setItem('userInfo', JSON.stringify(decoded));
    } catch {}
  }
  const normalizedAuthUserId = writeAuthUserId(payload?.authUserId);
  applyAuthUserIdHeader(axios.defaults.headers.common, normalizedAuthUserId);
  authUserId.value = normalizedAuthUserId;
};

// =============== 메시지 핸들러 ===============

const handleMessage = async (event) => {
  const data = parseMessage(event);
  if (!data) return;

  const { action, payload = {} } = data;

  if (action === 'hardwareBack') {
    const result = await appBackCoordinator.handleHardwareBack(payload);
    postToApp({ action: 'backResult', payload: result });
    return;
  }

  // init: 인증 설정 + ready 이후 페이지별 콜백
  if (action === 'init' || action === 'refresh') {
    setAuth(payload);
    // 앱이 전달한 guestInfoId를 sessionStorage에 저장 (웹 interceptor가 authUserId 헤더로 사용)
    if (payload?.guestInfoId) {
      sessionStorage.setItem('guestInfoId', payload.guestInfoId);
    }
    if (payload?.iapProducts) iapProducts.value = payload.iapProducts;
    if (payload?.accessToken) {
      try {
        const { useMyInfoStore } = await import('@/store/modules/myinfo');
        const myInfoStore = useMyInfoStore();
        myInfoStore.fetchMyInfo();
        myInfoStore.fetchUnreadAlarm();
      } catch {}
    }
    window.dispatchEvent(new CustomEvent('rn:init', { detail: { payload, action } }));

    // init 완료 후 대기 딥링크 처리
    if (!_authReady) {
      _authReady = true;
      if (_pendingDeeplinks.length > 0) {
        _pendingDeeplinks.forEach((dl) => {
          const webPath = resolveDeeplinkPath(dl);
          if (webPath && _router) _router.replace(webPath);
        });
        _pendingDeeplinks.length = 0;
      }
    }
  }

  // login: Vuex 스토어 동기화
  if (action === 'login') {
    setAuth(payload);
    const rawToken = String(payload.accessToken).replace(/^bearer\s+/i, '');
    store.commit('user/SET_TOKEN', rawToken);
    if (payload.authUserId) store.commit('user/SET_ID', payload.authUserId);
    // refresh_token이 함께 전달되면 sessionStorage/Vuex에 저장 (callback 페이지 등에서 활용)
    if (payload.refreshToken) {
      sessionStorage.setItem('refresh_token', payload.refreshToken);
      store.commit('user/SET_REFRESH', payload.refreshToken);
    }
    let decoded = null;
    try {
      decoded = jwtDecode(rawToken);
      if (decoded.name) store.commit('user/SET_NAME', decoded.name);
      if (decoded.id) store.commit('user/SET_DATA', decoded);
    } catch {}
    // 사용자 정보 + 알림 배지 갱신
    try {
      const { useMyInfoStore } = await import('@/store/modules/myinfo');
      const myInfoStore = useMyInfoStore();
      myInfoStore.fetchMyInfo();
      myInfoStore.fetchUnreadAlarm();
    } catch {}
    window.dispatchEvent(new CustomEvent('rn:init', { detail: { payload, action } }));

    // 소셜 로그인인데 약관 미완료(ROLE_USER 권한 없음) → /auth/callback으로 이동해 약관 플로우 진행
    if (payload?.provider && decoded) {
      const hasRoleUser =
        Array.isArray(decoded.authorities) && decoded.authorities.includes('ROLE_USER');
      if (!hasRoleUser) {
        const hashParams = new URLSearchParams({
          access_token: rawToken,
          token_type: 'bearer',
          refresh_token: payload.refreshToken ?? sessionStorage.getItem('refresh_token') ?? '',
          id: String(payload.authUserId ?? decoded.id ?? ''),
          name: decoded.name ?? '',
          action: 'join',
        });
        if (_router) {
          _router.replace({ path: '/auth/callback', hash: `#${hashParams.toString()}` });
        }
      }
    }
  }

  // withdraw: 탈퇴 응답. logout과 동일하게 상태 정리하되 router.push는 하지 않음
  // (delete.vue에서 rn:withdraw 이벤트 수신 후 withdraw-finish로 이동)
  if (action === 'withdraw') {
    accessToken.value = null;
    authUserId.value = null;
    store.dispatch('user/logout');
    axios.defaults.headers.common['Authorization'] = '';
    // 회원 정보 전체 초기화 (smartwel 로고, 복지찬스 포인트 카드 등 제휴 UI 정리 포함)
    try {
      const { useMyInfoStore } = await import('@/store/modules/myinfo');
      useMyInfoStore().resetMyInfo();
    } catch {}
    try {
      const { usePaymentsStore } = await import('@/store/modules/payments');
      usePaymentsStore().clearPaymentsInfo();
    } catch {}
    window.dispatchEvent(new CustomEvent('rn:closeMenu'));
    window.dispatchEvent(new CustomEvent('rn:withdraw'));
    return;
  }

  // logout: 세션 클리어 + 스토어 리셋
  if (action === 'logout') {
    accessToken.value = null;
    authUserId.value = null;
    store.dispatch('user/logout');
    axios.defaults.headers.common['Authorization'] = '';
    // 회원 정보 전체 초기화 (smartwel 로고, 복지찬스 포인트 카드 등 제휴 UI 정리 포함)
    try {
      const { useMyInfoStore } = await import('@/store/modules/myinfo');
      useMyInfoStore().resetMyInfo();
    } catch {}
    // 포인트/분석권 초기화
    try {
      const { usePaymentsStore } = await import('@/store/modules/payments');
      usePaymentsStore().clearPaymentsInfo();
    } catch {}
    window.dispatchEvent(new CustomEvent('rn:closeMenu'));
    window.dispatchEvent(new CustomEvent('rn:logout'));
    if (_router) _router.push('/main');
  }

  // closeMenu
  if (action === 'closeMenu') {
    window.dispatchEvent(new CustomEvent('rn:closeMenu'));
  }

  // deeplink
  if (action === 'deeplink') {
    const dlArgs = { source: payload?.source, path: payload?.path, deeplinkData: payload?.deeplinkData };
    const webPath = resolveDeeplinkPath(dlArgs);
    if (!webPath) return;

    // 중복 딥링크 무시 (같은 경로가 3초 이내 재수신)
    const now = Date.now();
    if (webPath === _lastDeeplinkPath && now - _lastDeeplinkTime < 3000) return;
    _lastDeeplinkPath = webPath;
    _lastDeeplinkTime = now;

    // 파트너 로그인 진입은 인증 전이라도 즉시 라우팅 (이 경로 자체가 로그인 플로우)
    const isPartnerEntry = webPath.startsWith('/partner/');

    // init 전이면 큐에 저장 (단, 파트너 로그인은 예외)
    const isReady = _authReady || !!sessionStorage.getItem('X-Token') || !!sessionStorage.getItem('guestInfoId');
    if (!isReady && !isPartnerEntry) {
      _pendingDeeplinks.push(dlArgs);
      return;
    }
    if (_router) _router.replace(webPath);
    return;
  }

  // reset
  if (action === 'reset') {
    window.dispatchEvent(new CustomEvent('rn:reset', { detail: payload }));
  }

  // close
  if (action === 'close') {
    window.dispatchEvent(new CustomEvent('rn:close', { detail: payload }));
  }

  // requestReload
  if (action === 'requestReload') {
    window.location.reload();
    return;
  }

  // 기타 액션은 커스텀 이벤트로 전파
  window.dispatchEvent(new CustomEvent('rn:custom', { detail: data }));
};

// =============== 글로벌 초기화 (App.vue에서 1회 호출) ===============

export const initRnMessage = (router) => {
  if (_initialized) return;
  _initialized = true;
  _router = router;
  appBackCoordinator.attachRouter(router);

  window.addEventListener('message', handleMessage);
  document.addEventListener('message', handleMessage);

  // ready 신호 전송
  postToApp({
    action: 'ready',
    payload: {
      capabilities: [APP_BACK_PROTOCOL],
      sessionId: appBackCoordinator.getSessionId(),
    },
  });
};

export const __resetRnMessageGlobalForTests = () => {
  window.removeEventListener('message', handleMessage);
  document.removeEventListener('message', handleMessage);
  _router = null;
  _initialized = false;
  _authReady = false;
  _pendingDeeplinks.length = 0;
  _lastDeeplinkPath = null;
  _lastDeeplinkTime = 0;
  appBackCoordinator.resetForTests();
};

// =============== 페이지별 구독 (composable) ===============

/**
 * 페이지에서 앱 메시지를 구독하는 composable
 * @param {Object} options
 * @param {Function} options.onInit - init/refresh/login 수신 시 콜백
 * @param {Function} options.onReset - reset 수신 시 콜백
 * @param {Function} options.onClose - close 수신 시 콜백
 * @param {Function} options.onCustom - 기타 액션 수신 시 콜백
 */
export const useRnMessage = ({ onInit, onReset, onClose, onCustom } = {}) => {
  const handleInit = (e) => onInit?.(e.detail.payload, e.detail.action);
  const handleReset = (e) => onReset?.(e.detail);
  const handleClose = (e) => onClose?.(e.detail);
  const handleCustom = (e) => onCustom?.(e.detail);

  onMounted(() => {
    if (onInit) window.addEventListener('rn:init', handleInit);
    if (onReset) window.addEventListener('rn:reset', handleReset);
    if (onClose) window.addEventListener('rn:close', handleClose);
    if (onCustom) window.addEventListener('rn:custom', handleCustom);
  });

  onUnmounted(() => {
    if (onInit) window.removeEventListener('rn:init', handleInit);
    if (onReset) window.removeEventListener('rn:reset', handleReset);
    if (onClose) window.removeEventListener('rn:close', handleClose);
    if (onCustom) window.removeEventListener('rn:custom', handleCustom);
  });

  return { accessToken, authUserId, iapProducts, setAuth };
};
